import { useState } from "react"
import { FilterBar } from "@/components/ui/filter-bar"
import { FilterButton } from "@/components/ui/filter-button"
import { FilterDropdown } from "@/components/ui/filter-dropdown"
import { GetGamesFilter } from "@/types/game"
import { cn } from "@/lib/utils"
import { SPORTS_OPTIONS } from "@/utils/game.utils"
import { ActionMenuItem } from "@/components/ui/action-menu"
import { toast } from "sonner"
import { Icon } from "@/components/ui/icon"

export type HomeContentFilter = "all" | "games" | "tournaments" | "training" | "events"

const CONTENT_FILTER_OPTIONS: {
  value: HomeContentFilter
  label: string
  icon: string
  countKey?: keyof NonNullable<DashboardFiltersProps["contentCounts"]>
}[] = [
  { value: "all", label: "Todos", icon: "grid_view" },
  { value: "games", label: "Jogos", icon: "sports_soccer", countKey: "games" },
  { value: "tournaments", label: "Torneios", icon: "emoji_events", countKey: "tournaments" },
  { value: "training", label: "Treinamentos", icon: "school", countKey: "training" },
  { value: "events", label: "Eventos", icon: "celebration", countKey: "events" },
]

interface DashboardFiltersProps {
  filters: GetGamesFilter
  onFilterChange: (filters: GetGamesFilter) => void
  contentFilter?: HomeContentFilter
  onContentFilterChange?: (filter: HomeContentFilter) => void
  contentCounts?: {
    games: number
    tournaments: number
    training: number
    events: number
  }
  pendingPaymentOnly?: boolean
  onPendingPaymentOnlyChange?: (active: boolean) => void
  pendingPaymentCount?: number
}

export function DashboardFilters({
  filters,
  onFilterChange,
  contentFilter = "all",
  onContentFilterChange,
  contentCounts,
  pendingPaymentOnly = false,
  onPendingPaymentOnlyChange,
  pendingPaymentCount = 0,
}: DashboardFiltersProps) {
  const handleSportChange = (sport: string | undefined) => {
    onFilterChange({ ...filters, sport })
  }

  type DateFilterValue = "today" | "week" | null
  const handleDateFilterChange = (value: DateFilterValue) => {
    if (!value) {
      const { startDate, endDate, ...rest } = filters
      onFilterChange(rest)
      return
    }
    const now = new Date()
    if (value === "today") {
      const start = new Date(now)
      start.setHours(0, 0, 0, 0)
      const end = new Date(now)
      end.setHours(23, 59, 59, 999)
      onFilterChange({ ...filters, startDate: start.toISOString(), endDate: end.toISOString() })
    } else {
      const start = new Date(now)
      start.setDate(start.getDate() - start.getDay())
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 6)
      end.setHours(23, 59, 59, 999)
      onFilterChange({ ...filters, startDate: start.toISOString(), endDate: end.toISOString() })
    }
  }

  const dateFilterValue: DateFilterValue = filters.startDate && filters.endDate
    ? (() => {
        const start = new Date(filters.startDate)
        const end = new Date(filters.endDate)
        const sameDay = start.toDateString() === end.toDateString()
        return sameDay ? "today" : "week"
      })()
    : null

  // Use the shared sports list
  const sports = SPORTS_OPTIONS
  
  const activeSport = sports.find(s => s.value === filters.sport)
  const activeSportLabel = activeSport?.label || "Todos os Esportes"
  const activeSportIcon = activeSport?.icon || "sports_soccer"

  const getSportColorClass = (sportValue?: string) => {
      switch(sportValue) {
          case 'FUTEBOL': return "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent shadow-emerald-500/20"
          case 'VOLEI': return "bg-amber-500 hover:bg-amber-600 text-white border-transparent shadow-amber-500/20"
          case 'BEACH_TENNIS': return "bg-orange-500 hover:bg-orange-600 text-white border-transparent shadow-orange-500/20"
          case 'CARIMBA': return "bg-purple-500 hover:bg-purple-600 text-white border-transparent shadow-purple-500/20"
          case 'CORRIDA': return "bg-sky-500 hover:bg-sky-600 text-white border-transparent shadow-sky-500/20"
          default: return "bg-muted/300 hover:bg-muted-foreground/60 text-white border-transparent shadow-black/10"
      }
  }

  const dateFilterLabel = dateFilterValue === "today" ? "Hoje" : dateFilterValue === "week" ? "Essa semana" : "Data"

  const activeContent =
    CONTENT_FILTER_OPTIONS.find((option) => option.value === contentFilter) ??
    CONTENT_FILTER_OPTIONS[0]

  const isNearMeActive = !!(filters.latitude && filters.longitude)
  const [isGettingLocation, setIsGettingLocation] = useState(false)

  const handleNearMeClick = () => {
    if (isNearMeActive) {
      const { latitude, longitude, distance, ...rest } = filters
      onFilterChange(rest)
      return
    }

    if (!navigator.geolocation) {
      toast.error("Seu navegador não suporta geolocalização")
      return
    }

    setIsGettingLocation(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        onFilterChange({
          ...filters,
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
          distance: "10",
        })
        toast.success("Mostrando jogos próximos a você (10 km)")
        setIsGettingLocation(false)
      },
      (error) => {
        setIsGettingLocation(false)
        if (error.code === 1) {
          toast.error("Permissão de localização negada. Ative nas configurações do navegador.")
        } else {
          toast.error("Não foi possível obter sua localização. Tente novamente.")
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  }

  return (
    <FilterBar>
      {onPendingPaymentOnlyChange && pendingPaymentCount > 0 ? (
        <FilterButton
          icon="payments"
          label={
            pendingPaymentCount > 1
              ? `Pagamento pendente (${pendingPaymentCount})`
              : "Pagamento pendente"
          }
          active={pendingPaymentOnly}
          variant="primary"
          onClick={() => onPendingPaymentOnlyChange(!pendingPaymentOnly)}
          className={
            pendingPaymentOnly
              ? "bg-amber-500 hover:bg-amber-600 text-white border-transparent shadow-amber-500/20"
              : "border-amber-300/60 dark:border-amber-700/50 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30"
          }
        />
      ) : null}

      {onContentFilterChange ? (
        <FilterDropdown
          trigger={
            <FilterButton
              icon={activeContent.icon}
              label={activeContent.label}
              active
              variant="primary"
              iconPosition="left"
            />
          }
        >
          {CONTENT_FILTER_OPTIONS.map((option) => {
            const count =
              option.countKey && contentCounts ? contentCounts[option.countKey] : undefined
            const isActive = contentFilter === option.value

            return (
              <ActionMenuItem
                key={option.value}
                onClick={() => onContentFilterChange(option.value)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
                  isActive && "bg-primary/5 text-primary")}
              >
                <div
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
                    isActive && "bg-white dark:bg-background text-primary shadow-sm")}
                >
                  <Icon name={option.icon} />
                </div>
                <span className="text-sm font-normal flex-1">{option.label}</span>
                {count !== undefined && count > 0 ? (
                  <span className="text-xs font-bold tabular-nums text-muted-foreground group-hover:text-primary">
                    {count}
                  </span>
                ) : null}
              </ActionMenuItem>
            )
          })}
        </FilterDropdown>
      ) : null}

      <FilterDropdown
        trigger={
            <FilterButton
                icon={activeSportIcon}
                label={activeSportLabel}
                active={true}
                variant="primary"
                iconPosition="left"
                className={activeSport ? getSportColorClass(activeSport.value) : undefined}
            />
        }
      >
        <ActionMenuItem 
          onClick={() => handleSportChange(undefined)} 
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
            !filters.sport && "bg-primary/5 text-primary"
          )}
        >
            <div className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
              !filters.sport && "bg-white dark:bg-background text-primary shadow-sm"
            )}>
              <Icon name="sports_soccer" />
            </div>
            <span className="text-sm font-normal">Todos os Esportes</span>
        </ActionMenuItem>
        {sports.map(sport => (
            <ActionMenuItem 
                key={sport.value} 
                onClick={() => handleSportChange(sport.value)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
                  filters.sport === sport.value && "bg-primary/5 text-primary"
                )}
            >
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
                  filters.sport === sport.value && "bg-white dark:bg-background text-primary shadow-sm"
                )}>
                  <Icon name={sport.icon} />
                </div>
                <span className="text-sm font-normal">{sport.label}</span>
            </ActionMenuItem>
        ))}
      </FilterDropdown>

      <FilterDropdown
        trigger={
            <FilterButton
                icon="calendar_today"
                label={dateFilterLabel}
                active={!!dateFilterValue}
                variant="primary"
            />
        }
      >
        <ActionMenuItem 
          onClick={() => handleDateFilterChange(null)} 
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
            !dateFilterValue && "bg-primary/5 text-primary"
          )}
        >
            <div className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
              !dateFilterValue && "bg-white dark:bg-background text-primary shadow-sm"
            )}>
              <Icon name="event" />
            </div>
            <span className="text-sm font-normal">Todos</span>
        </ActionMenuItem>
        <ActionMenuItem 
          onClick={() => handleDateFilterChange("today")}
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
            dateFilterValue === "today" && "bg-primary/5 text-primary"
          )}
        >
            <div className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
              dateFilterValue === "today" && "bg-white dark:bg-background text-primary shadow-sm"
            )}>
              <Icon name="today" />
            </div>
            <span className="text-sm font-normal">Hoje</span>
        </ActionMenuItem>
        <ActionMenuItem 
          onClick={() => handleDateFilterChange("week")}
          className={cn(
            "flex items-center gap-3 px-4 py-2.5 my-1 text-muted-foreground dark:text-muted-foreground hover:text-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all duration-200 group",
            dateFilterValue === "week" && "bg-primary/5 text-primary"
          )}
        >
            <div className={cn(
              "flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 dark:bg-muted text-muted-foreground dark:text-muted-foreground group-hover:bg-white dark:group-hover:bg-[#111418] group-hover:text-primary group-hover:shadow-sm transition-all duration-200",
              dateFilterValue === "week" && "bg-white dark:bg-background text-primary shadow-sm"
            )}>
              <Icon name="date_range" />
            </div>
            <span className="text-sm font-normal">Essa semana</span>
        </ActionMenuItem>
      </FilterDropdown>

      <FilterButton
        icon="location_on"
        label={isGettingLocation ? "Obtendo localização..." : "Próximo a mim"}
        active={isNearMeActive}
        variant="primary"
        onClick={handleNearMeClick}
        disabled={isGettingLocation}
      />
    </FilterBar>
  )
}
